import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Scissors, Type, Play, Pause } from "lucide-react";

const SUB_STYLES = [
    { id: "hormozi", name: "Hormozi Style", color: "#FFB800", size: "5xl", weight: "900" },
    { id: "neon",    name: "Neon Pop",      color: "#FF4D00", size: "4xl", weight: "800" },
    { id: "min",     name: "Minimalist",    color: "#F1F5F9", size: "3xl", weight: "600" },
    { id: "cyber",   name: "Cyber Punk",    color: "#0099FF", size: "5xl", weight: "800" },
];

export default function EditingTemplates() {
    const { lang } = useApp();
    const [style, setStyle] = useState("hormozi");
    const [hookText, setHookText] = useState(lang === "fr" ? "Personne n'osait le faire..." : "Nobody dared to do it...");
    const active = SUB_STYLES.find((s) => s.id === style);

    return (
        <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-4">
                <div>
                    <div className="overline">07 · Editing</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Templates montage 9:16" : "9:16 editing templates"}</h2>
                    <p className="text-sm text-slate-400 mt-2">{lang === "fr" ? "Aperçu live du sous-titrage et du placement du hook. Exporte le preset vers CapCut/Canva." : "Live preview of subtitles and hook placement. Export the preset to CapCut/Canva."}</p>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Style sous-titres" : "Subtitle style"}</label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {SUB_STYLES.map((s) => (
                            <button key={s.id} onClick={() => setStyle(s.id)}
                                className={`p-3 rounded border kinetic text-left ${style === s.id ? "border-[#FF4D00] bg-[#FF4D00]/10" : "border-[#253046] hover:border-[#3B4B6C]"}`}>
                                <div className="font-display font-bold uppercase text-sm">{s.name}</div>
                                <div className="text-[10px] font-mono-sf text-slate-500 mt-1">{s.color}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Texte du hook" : "Hook text"}</label>
                    <input value={hookText} onChange={(e) => setHookText(e.target.value)}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                </div>

                <div className="bg-[#161D2A] border border-[#253046] rounded p-4">
                    <div className="overline">Timeline layout</div>
                    <div className="mt-3 space-y-1.5 text-xs">
                        {[
                            { t: "00:00 - 00:03", label: lang === "fr" ? "Hook (retention +40%)" : "Hook (retention +40%)", color: "#FF4D00" },
                            { t: "00:03 - 00:15", label: lang === "fr" ? "Contexte" : "Context", color: "#FFB800" },
                            { t: "00:15 - 00:45", label: lang === "fr" ? "Développement" : "Development", color: "#0099FF" },
                            { t: "00:45 - 00:60", label: "CTA + payoff", color: "#10B981" },
                        ].map((r) => (
                            <div key={r.t} className="flex items-center gap-3">
                                <span className="font-mono-sf text-[10px] text-slate-500 w-24">{r.t}</span>
                                <div className="flex-1 h-6 rounded flex items-center px-2 text-[10px] font-medium" style={{ background: `${r.color}20`, color: r.color, borderLeft: `2px solid ${r.color}` }}>
                                    {r.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 9:16 preview */}
            <div className="lg:col-span-3 flex justify-center items-start pt-4">
                <div className="relative">
                    <div className="absolute -inset-2 flame-gradient rounded-3xl blur-2xl opacity-15" />
                    <div className="relative w-[280px] sm:w-[320px] aspect-[9/16] rounded-2xl border-2 border-[#253046] bg-gradient-to-b from-[#161D2A] via-[#0F141C] to-[#080B10] overflow-hidden shadow-2xl scanline">
                        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 border border-red-500/40">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full rec-dot" />
                            <span className="text-[10px] font-mono-sf text-red-400">REC · 00:03</span>
                        </div>
                        <div className="absolute top-3 right-3 text-[10px] font-mono-sf px-2 py-0.5 rounded bg-black/60 border border-[#253046] text-slate-300">9:16</div>

                        {/* Hook overlay center */}
                        <div className="absolute inset-0 flex items-center justify-center p-6">
                            <div className="text-center leading-none" style={{ color: active.color, fontWeight: active.weight, textShadow: "0 4px 20px rgba(0,0,0,.9), 0 0 40px rgba(0,0,0,.6)" }}>
                                <div className="font-display uppercase" style={{ fontSize: active.id === "min" ? "1.8rem" : "2.4rem" }}>{hookText}</div>
                            </div>
                        </div>

                        {/* CTA bottom */}
                        <div className="absolute bottom-14 left-4 right-4">
                            <div className="text-[10px] font-mono-sf text-[#FF4D00] uppercase tracking-widest">→ CTA slot</div>
                            <div className="text-xs text-white font-semibold mt-1">Follow @gastonlechat 🐾</div>
                        </div>

                        <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                            <Play className="w-5 h-5 text-white" fill="currentColor" />
                            <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                                <div className="h-full flame-gradient" style={{ width: "5%" }} />
                            </div>
                            <span className="font-mono-sf text-[10px] text-white/70">00:03 / 00:47</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
